/**
 * Balatro 扑克引擎 — 纯函数
 * 牌型判定、算分、牌组构建、阈值检查
 */

import type {
  PlayingCard,
  Suit,
  Rank,
  PokerHandType,
  HandTypeLevel,
  HandEvaluation,
  HandScoreResult,
  JokerEffect,
  ScoringStep,
  Joker,
  ClueCard,
  ClauseThreshold,
  CardEnhancement,
  StakeholderId,
  BossEffect,
} from './types'

import {
  STAKEHOLDER_TO_SUIT,
  DEPTH_TO_RANKS,
  RANK_CHIP_VALUE,
  RANK_DISPLAY,
  VALID_STRAIGHTS,
  HAND_TYPE_CONFIG,
  ALL_RANKS,
  ALL_SUITS,
  SUIT_DISPLAY,
} from '../data/pokerConfig'

// =============================================================================
// 标准 52 张牌组构建
// =============================================================================

/**
 * 构建标准 52 张牌组 (4花色 × 13点数)
 * 收集的 ClueCard 对匹配的牌施加增强 (foil)
 */
export function buildDeck(clueCards: ClueCard[]): PlayingCard[] {
  // 1. 生成 52 张基础牌
  const deck: PlayingCard[] = []
  for (const suit of ALL_SUITS) {
    for (const rank of ALL_RANKS) {
      deck.push({
        id: `pc_${suit}_${rank}`,
        clueCardId: null,
        name: `${RANK_DISPLAY[rank]}·${SUIT_DISPLAY[suit].name}`,
        suit,
        rank,
        chipValue: RANK_CHIP_VALUE[rank],
        enhancement: 'none' as CardEnhancement,
        bonusChips: 0,
        bonusMult: 0,
      })
    }
  }

  // 2. 用 ClueCard 增强匹配的牌
  const groupCounters: Record<string, number> = {}
  for (const card of clueCards) {
    const source = card.source as StakeholderId | 'synthesis'
    const suit = STAKEHOLDER_TO_SUIT[source] ?? 'wild'
    const depth = card.depth
    const ranks = DEPTH_TO_RANKS[depth] ?? DEPTH_TO_RANKS[0]
    const groupKey = `${source}_${depth}`
    const idx = groupCounters[groupKey] ?? 0
    groupCounters[groupKey] = idx + 1
    const rank: Rank = ranks![idx % ranks!.length]!

    // 找到匹配的牌并增强
    const match = deck.find(c => c.suit === suit && c.rank === rank && c.enhancement === 'none')
    if (match) {
      match.clueCardId = card.id
      match.name = card.name
      match.enhancement = 'foil'
      match.bonusChips = 10
    } else if (suit === 'wild') {
      // wild 卡作为额外牌加入
      deck.push({
        id: `pc_${card.id}`,
        clueCardId: card.id,
        name: card.name,
        suit: 'wild',
        rank,
        chipValue: RANK_CHIP_VALUE[rank],
        enhancement: 'foil' as CardEnhancement,
        bonusChips: 10,
        bonusMult: 0,
      })
    }
  }

  return deck
}

// =============================================================================
// 洗牌
// =============================================================================

export function shuffleDeck<T>(deck: T[]): T[] {
  const shuffled = [...deck]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!]
  }
  return shuffled
}

// =============================================================================
// 牌型判定
// =============================================================================

/**
 * 判定一手牌(1-5张)的最佳牌型
 * wild 花色视为同时属于所有花色
 */
export function evaluatePokerHand(cards: PlayingCard[]): HandEvaluation {
  if (cards.length === 0) {
    return { handType: 'HIGH_CARD', scoringCards: [], kickers: [] }
  }

  // 按 rank 分组 (point number → cards)
  const rankGroups = new Map<Rank, PlayingCard[]>()
  for (const card of cards) {
    const group = rankGroups.get(card.rank) ?? []
    group.push(card)
    rankGroups.set(card.rank, group)
  }

  // 排序: 按组大小降序, 再按 rank 降序
  const groups = [...rankGroups.entries()]
    .sort((a, b) => {
      if (b[1].length !== a[1].length) return b[1].length - a[1].length
      return b[0] - a[0]
    })

  const groupSizes = groups.map(g => g[1].length)

  // 检查同花 (wild 匹配任意花色)
  const isFlush = cards.length >= 5 && checkFlush(cards)

  // 检查顺子
  const isStraight = cards.length >= 5 && checkStraight(cards)

  // 判定牌型 (从高到低)
  let handType: PokerHandType
  let scoringCards: PlayingCard[]
  let kickers: PlayingCard[]

  if (groupSizes[0]! >= 5) {
    // Five of a Kind
    handType = 'FIVE_OF_A_KIND'
    scoringCards = groups[0]![1].slice(0, 5)
    kickers = []
  } else if (isFlush && isStraight) {
    handType = 'STRAIGHT_FLUSH'
    scoringCards = [...cards]
    kickers = []
  } else if (groupSizes[0]! >= 4) {
    handType = 'FOUR_OF_A_KIND'
    scoringCards = groups[0]![1].slice(0, 4)
    kickers = cards.filter(c => !scoringCards.includes(c))
  } else if (groupSizes[0]! >= 3 && groupSizes[1]! >= 2) {
    handType = 'FULL_HOUSE'
    scoringCards = [...groups[0]![1].slice(0, 3), ...groups[1]![1].slice(0, 2)]
    kickers = []
  } else if (isFlush) {
    handType = 'FLUSH'
    scoringCards = [...cards]
    kickers = []
  } else if (isStraight) {
    handType = 'STRAIGHT'
    scoringCards = [...cards]
    kickers = []
  } else if (groupSizes[0]! >= 3) {
    handType = 'THREE_OF_A_KIND'
    scoringCards = groups[0]![1].slice(0, 3)
    kickers = cards.filter(c => !scoringCards.includes(c))
  } else if (groupSizes[0]! >= 2 && groupSizes[1]! >= 2) {
    handType = 'TWO_PAIR'
    scoringCards = [...groups[0]![1].slice(0, 2), ...groups[1]![1].slice(0, 2)]
    kickers = cards.filter(c => !scoringCards.includes(c))
  } else if (groupSizes[0]! >= 2) {
    handType = 'PAIR'
    scoringCards = groups[0]![1].slice(0, 2)
    kickers = cards.filter(c => !scoringCards.includes(c))
  } else {
    handType = 'HIGH_CARD'
    // Highest rank card is the scoring card
    const sorted = [...cards].sort((a, b) => b.rank - a.rank)
    scoringCards = [sorted[0]!]
    kickers = sorted.slice(1)
  }

  return { handType, scoringCards, kickers }
}

/** 检查 5 张牌是否构成同花 (wild 匹配任意花色) */
function checkFlush(cards: PlayingCard[]): boolean {
  const nonWild = cards.filter(c => c.suit !== 'wild')
  if (nonWild.length === 0) return true // all wild = flush

  const targetSuit = nonWild[0]!.suit
  return nonWild.every(c => c.suit === targetSuit)
}

/** 检查 5 张牌是否构成顺子 */
function checkStraight(cards: PlayingCard[]): boolean {
  const ranks = cards.map(c => c.rank)
  const rankSet = new Set(ranks)

  for (const straight of VALID_STRAIGHTS) {
    if (straight.every(r => rankSet.has(r))) {
      return true
    }
  }
  return false
}

// =============================================================================
// 算分
// =============================================================================

/**
 * Balatro 标准算分:
 * 1. base chips/mult from hand type level
 * 2. scoring cards add their chipValue + bonusChips/bonusMult
 * 3. enhancement multipliers
 * 4. held cards (steel enhancement)
 * 5. Joker effects (left → right)
 * 6. final = ceil(chips × mult)
 */
export function scoreHand(
  evaluation: HandEvaluation,
  level: HandTypeLevel,
  equippedJokers: Joker[],
  heldCards: PlayingCard[],
  deckSize: number,
  reversedCount: number,
  handsPlayed: number,
  bossEffect?: BossEffect | null,
): HandScoreResult {
  // Boss: halve_base
  let totalChips = bossEffect?.type === 'halve_base' ? Math.floor(level.baseChips / 2) : level.baseChips
  let totalMult = bossEffect?.type === 'halve_base' ? Math.floor(level.baseMult / 2) : level.baseMult
  const jokerEffects: JokerEffect[] = []

  // 1. Scoring cards
  for (const card of evaluation.scoringCards) {
    // Boss: debuff_suit — skip cards of debuffed suit
    if (bossEffect?.type === 'debuff_suit' && bossEffect.debuffedSuit && card.suit === bossEffect.debuffedSuit) continue
    // Boss: debuff_face — skip K cards
    if (bossEffect?.type === 'debuff_face' && card.rank === 13) continue

    totalChips += card.chipValue + card.bonusChips
    totalMult += card.bonusMult

    // Enhancement multipliers on scoring cards
    if (card.enhancement === 'polychrome') totalMult *= 1.5
    if (card.enhancement === 'glass') totalMult *= 2.0
  }

  // 2. Held cards (steel enhancement)
  for (const card of heldCards) {
    if (card.enhancement === 'steel') totalMult *= 1.5
  }

  // 3. Joker effects (left → right order)
  for (const joker of equippedJokers) {
    const effect = applyJokerEffect(
      joker,
      evaluation,
      totalChips,
      totalMult,
      deckSize,
      reversedCount,
      handsPlayed,
    )
    if (effect) {
      jokerEffects.push(effect)
      if (effect.type === 'add_chips') totalChips += effect.value
      else if (effect.type === 'add_mult') totalMult += effect.value
      else if (effect.type === 'mult_mult') totalMult *= effect.value
    }
  }

  const handScore = Math.ceil(totalChips * totalMult)

  return {
    evaluation,
    level,
    totalChips,
    totalMult,
    handScore,
    jokerEffects,
    scoringSteps: [],
  }
}

/**
 * Balatro 逐步算分 (记录每一步到 ScoringStep[])
 * 逻辑与 scoreHand() 完全相同，但额外记录动画步骤
 */
export function scoreHandDetailed(
  evaluation: HandEvaluation,
  level: HandTypeLevel,
  equippedJokers: Joker[],
  heldCards: PlayingCard[],
  deckSize: number,
  reversedCount: number,
  handsPlayed: number,
  bossEffect?: BossEffect | null,
): HandScoreResult {
  // Boss: halve_base
  const effectiveBaseChips = bossEffect?.type === 'halve_base' ? Math.floor(level.baseChips / 2) : level.baseChips
  const effectiveBaseMult = bossEffect?.type === 'halve_base' ? Math.floor(level.baseMult / 2) : level.baseMult
  let totalChips = effectiveBaseChips
  let totalMult = effectiveBaseMult
  const jokerEffects: JokerEffect[] = []
  const scoringSteps: ScoringStep[] = []

  const config = HAND_TYPE_CONFIG[evaluation.handType]

  // Step 0: hand type base
  scoringSteps.push({
    type: 'hand_type_base',
    handType: evaluation.handType,
    handTypeCN: config.nameCN,
    level: level.level,
    baseChips: effectiveBaseChips,
    baseMult: effectiveBaseMult,
    runningChips: totalChips,
    runningMult: totalMult,
  })

  // 1. Scoring cards
  for (const card of evaluation.scoringCards) {
    // Boss: debuff_suit — skip cards of debuffed suit
    if (bossEffect?.type === 'debuff_suit' && bossEffect.debuffedSuit && card.suit === bossEffect.debuffedSuit) continue
    // Boss: debuff_face — skip K cards
    if (bossEffect?.type === 'debuff_face' && card.rank === 13) continue

    totalChips += card.chipValue + card.bonusChips
    totalMult += card.bonusMult

    let enhancementMultiplier = 1
    if (card.enhancement === 'polychrome') {
      totalMult *= 1.5
      enhancementMultiplier = 1.5
    }
    if (card.enhancement === 'glass') {
      totalMult *= 2.0
      enhancementMultiplier = 2.0
    }

    scoringSteps.push({
      type: 'card_score',
      cardId: card.id,
      chipValue: card.chipValue,
      bonusChips: card.bonusChips,
      bonusMult: card.bonusMult,
      enhancement: card.enhancement,
      enhancementMultiplier,
      runningChips: totalChips,
      runningMult: totalMult,
    })
  }

  // 2. Held cards (steel enhancement)
  for (const card of heldCards) {
    if (card.enhancement === 'steel') {
      totalMult *= 1.5
      scoringSteps.push({
        type: 'held_card',
        cardId: card.id,
        enhancement: card.enhancement,
        multiplier: 1.5,
        runningChips: totalChips,
        runningMult: totalMult,
      })
    }
  }

  // 3. Joker effects (left → right order)
  for (const joker of equippedJokers) {
    const effect = applyJokerEffect(
      joker,
      evaluation,
      totalChips,
      totalMult,
      deckSize,
      reversedCount,
      handsPlayed,
    )
    if (effect) {
      jokerEffects.push(effect)
      if (effect.type === 'add_chips') totalChips += effect.value
      else if (effect.type === 'add_mult') totalMult += effect.value
      else if (effect.type === 'mult_mult') totalMult *= effect.value

      scoringSteps.push({
        type: 'joker_effect',
        jokerId: effect.jokerId,
        jokerName: effect.jokerName,
        effectType: effect.type,
        value: effect.value,
        runningChips: totalChips,
        runningMult: totalMult,
      })
    }
  }

  const handScore = Math.ceil(totalChips * totalMult)

  // Final step
  scoringSteps.push({
    type: 'final_score',
    totalChips,
    totalMult,
    handScore,
  })

  return {
    evaluation,
    level,
    totalChips,
    totalMult,
    handScore,
    jokerEffects,
    scoringSteps,
  }
}

/** 应用单个 Joker 效果 */
function applyJokerEffect(
  joker: Joker,
  evaluation: HandEvaluation,
  _chips: number,
  _mult: number,
  deckSize: number,
  reversedCount: number,
  handsPlayed: number,
): JokerEffect | null {
  const { handType, scoringCards } = evaluation

  switch (joker.id) {
    case 'J01': // 关键证人: +15 Chips
      return { jokerId: 'J01', jokerName: joker.name, type: 'add_chips', value: 15 }

    case 'J02': // 深度调查: K(13) 计分时 +15 Chips each
    {
      const kCount = scoringCards.filter(c => c.rank === 13).length
      return kCount > 0
        ? { jokerId: 'J02', jokerName: joker.name, type: 'add_chips', value: 15 * kCount }
        : null
    }

    case 'J03': // 审计线索: A(14) 计分时 +30 Chips each
    {
      const aCount = scoringCards.filter(c => c.rank === 14).length
      return aCount > 0
        ? { jokerId: 'J03', jokerName: joker.name, type: 'add_chips', value: 30 * aCount }
        : null
    }

    case 'J04': // 内部消息: 牌组剩余卡数 × +1 Chips
      return { jokerId: 'J04', jokerName: joker.name, type: 'add_chips', value: deckSize * 1 }

    case 'J05': // 法律顾问: +4 Mult
      return { jokerId: 'J05', jokerName: joker.name, type: 'add_mult', value: 4 }

    case 'J06': // 判例援引: 含 Pair 时 +3 Mult
    {
      const hasPair = handType === 'PAIR' || handType === 'TWO_PAIR' ||
                      handType === 'FULL_HOUSE' || handType === 'FOUR_OF_A_KIND' ||
                      handType === 'FIVE_OF_A_KIND'
      return hasPair
        ? { jokerId: 'J06', jokerName: joker.name, type: 'add_mult', value: 3 }
        : null
    }

    case 'J07': // 谈判专家: 每打出一手牌 +2 Mult（递增）
      return { jokerId: 'J07', jokerName: joker.name, type: 'add_mult', value: 2 * (handsPlayed + 1) }

    case 'J08': // 证据链: 3+ 同花色计分时 ×1.5 Mult
    {
      const suitCounts = new Map<Suit, number>()
      for (const c of scoringCards) {
        suitCounts.set(c.suit, (suitCounts.get(c.suit) ?? 0) + 1)
      }
      const hasThreeSameSuit = [...suitCounts.values()].some(count => count >= 3)
      return hasThreeSameSuit
        ? { jokerId: 'J08', jokerName: joker.name, type: 'mult_mult', value: 1.5 }
        : null
    }

    case 'J09': // 合同漏洞: Two Pair 或 Full House 时 ×1.5 Mult
      return (handType === 'TWO_PAIR' || handType === 'FULL_HOUSE')
        ? { jokerId: 'J09', jokerName: joker.name, type: 'mult_mult', value: 1.5 }
        : null

    case 'J10': // 尽职调查: ×1.5 Mult（已过 2+ 阈值时）
      return reversedCount >= 2
        ? { jokerId: 'J10', jokerName: joker.name, type: 'mult_mult', value: 1.5 }
        : null

    case 'J11': // 交叉质询: 出牌中每种不同花色 +3 Mult
    {
      const uniqueSuits = new Set(scoringCards.map(c => c.suit))
      // wild 不计为单独花色 (它已经在牌型判定时被计入了)
      const suitCount = uniqueSuits.size
      return suitCount > 0
        ? { jokerId: 'J11', jokerName: joker.name, type: 'add_mult', value: 3 * suitCount }
        : null
    }

    // J12 (万能证人) 在牌型判定阶段处理，不在算分阶段
    // J13 (不可抗力) 和 J14 (缓兵之计) 在进入质证时处理
    // J15 (和解方案) 在阈值检查时处理

    default:
      return null
  }
}

// =============================================================================
// 阈值检查
// =============================================================================

/**
 * 检查是否突破了新的条款阈值
 * 返回新突破的 clauseId 列表
 */
export function checkMilestones(
  runningScore: number,
  thresholds: ClauseThreshold[],
  hasSettlement: boolean,
): string[] {
  const newlyReversed: string[] = []

  for (const t of thresholds) {
    if (t.reversed) continue
    const effectiveThreshold = hasSettlement ? Math.floor(t.threshold * 0.85) : t.threshold
    if (runningScore >= effectiveThreshold) {
      newlyReversed.push(t.clauseId)
    }
  }

  return newlyReversed
}

// =============================================================================
// 手牌预览 (UI 用)
// =============================================================================

/** 预览当前选中的牌会形成什么牌型 */
export function previewHandType(selectedCards: PlayingCard[]): {
  handType: PokerHandType
  handTypeName: string
  handTypeCN: string
} | null {
  if (selectedCards.length === 0) return null

  const evaluation = evaluatePokerHand(selectedCards)
  const config = HAND_TYPE_CONFIG[evaluation.handType]

  return {
    handType: evaluation.handType,
    handTypeName: config.name,
    handTypeCN: config.nameCN,
  }
}

/** 预览选中的牌的牌型基础分 (用于侧栏实时显示，不含卡牌/Joker 加成) */
export function previewHandScore(
  selectedCards: PlayingCard[],
  handTypeLevels: Record<PokerHandType, HandTypeLevel>,
): { handTypeCN: string; level: number; chips: number; mult: number } | null {
  if (selectedCards.length === 0) return null

  const evaluation = evaluatePokerHand(selectedCards)
  const config = HAND_TYPE_CONFIG[evaluation.handType]
  const level = handTypeLevels[evaluation.handType]

  return {
    handTypeCN: config.nameCN,
    level: level.level,
    chips: level.baseChips,
    mult: level.baseMult,
  }
}

// =============================================================================
// Rank 升级 (塔罗牌 T02 用)
// =============================================================================

const RANK_UPGRADE_ORDER: Rank[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]

export function upgradeRank(rank: Rank): Rank {
  const idx = RANK_UPGRADE_ORDER.indexOf(rank)
  if (idx < 0 || idx >= RANK_UPGRADE_ORDER.length - 1) return rank
  return RANK_UPGRADE_ORDER[idx + 1]!
}

